import React, { useState, useCallback, useEffect, useRef } from "react";
import InviteMatchingModal from "./InviteMatchingModal";

// ── Design tokens (match console App.js) ─────────────────────────────────────
const C = {
  bg:"#06080F", surface:"#0e1117", card:"#141820", border:"#1e2533",
  accent:"#F59E0B", accentDim:"#F59E0B22", text:"#F1F5F9", muted:"#64748B",
  danger:"#EF4444", success:"#22C55E", info:"#3B82F6", purple:"#8B5CF6",
};

// ── Constants ─────────────────────────────────────────────────────────────────
const AGENTS = ["jal","vayu","agni","prithvi","aakash"];

const AGENT_META = {
  jal: {
    name:"Jal", gender:"female", element:"Water", color:"#06B6D4",
    mode:"Empathy Mode", tagline:"Rapport Building",
    description:"Warm and unhurried. Creates emotional safety for honest sharing.",
    bestFor:["Consumer Emotion","Healthcare","Financial Stress","Life Decisions"],
    avatar:"/avatars/jal.png",
    recommendedFor:["csat","custom"],
  },
  vayu: {
    name:"Vayu", gender:"female", element:"Air", color:"#A78BFA",
    mode:"Discovery Mode", tagline:"Open Exploration",
    description:"Light and curious. Uncovers what participants think before being primed.",
    bestFor:["Unaided Awareness","New Categories","Early Concept Testing","Exploratory"],
    avatar:"/avatars/vayu.png",
    recommendedFor:["concept_testing","ux_research","market_sizing"],
  },
  agni: {
    name:"Agni", gender:"male", element:"Fire", color:"#F59E0B",
    mode:"Insight Mode", tagline:"Depth Probing",
    description:"Warm but probing. Surfaces the reasoning behind initial answers.",
    bestFor:["Brand Loyalty","Competitor Analysis","NPS","Product Pain Points"],
    avatar:"/avatars/agni.png",
    recommendedFor:["brand_tracking","nps","ad_testing"],
  },
  prithvi: {
    name:"Prithvi", gender:"male", element:"Earth", color:"#22C55E",
    mode:"Clarity Mode", tagline:"Validation",
    description:"Calm and structured. Ensures data is clean, precise and actionable.",
    bestFor:["Usage Habits","Purchase Behaviour","Satisfaction Scoring","Claims Testing"],
    avatar:"/avatars/prithvi.png",
    recommendedFor:["product_feedback","csat","ces"],
  },
  aakash: {
    name:"Aakash", gender:"male", element:"Space", color:"#6366F1",
    mode:"Reflection Mode", tagline:"Deep Thinking",
    description:"Spacious and thoughtful. Helps participants access considered views.",
    bestFor:["Future Trends","Innovation","Life Stage Transitions","Long-horizon Decisions"],
    avatar:"/avatars/aakash.png",
    recommendedFor:["market_sizing","ux_research"],
  },
};
const MODALITIES = ["voice","chat","video"];
// BCP47 codes keyed by human-readable name — filtered at runtime by /api/config supportedLanguages
const LANGUAGE_BCP47_MAP = {
  "English":"en-US","Hindi":"hi-IN","Tamil":"ta-IN","Telugu":"te-IN",
  "Marathi":"mr-IN","Bengali":"bn-IN","Gujarati":"gu-IN","Kannada":"kn-IN",
  "Malayalam":"ml-IN","Punjabi":"pa-IN","Odia":"or-IN","Other":"en-US",
};
// Category = research DOMAIN (organization only). Methodologies live in SURVEY_TYPES.
const CATEGORIES = ["Brand","Product","Customer Experience","Advertising","Innovation","Market Understanding","UX / Design","Employee","Political","Other"];
// Survey Type = METHODOLOGY — mirrors api/config/studyTypes.js (drives the compiler + report).
const SURVEY_TYPES = [
  { value:"nps",              label:"Net Promoter Score (NPS)" },
  { value:"csat",             label:"Customer Satisfaction (CSAT)" },
  { value:"ces",              label:"Customer Effort Score (CES)" },
  { value:"brand_tracking",   label:"Brand Tracking" },
  { value:"product_feedback", label:"Product Feedback" },
  { value:"concept_testing",  label:"Concept Testing" },
  { value:"ad_testing",       label:"Ad Testing" },
  { value:"market_sizing",    label:"Market Sizing (U&A)" },
  { value:"ux_research",      label:"UX Research" },
  { value:"custom",           label:"Custom / Exploratory" },
];
const QUESTION_TYPES = ["open","rating_scale","binary","ranking"];
const INCENTIVE_TYPES = ["none","cash","voucher","points","lucky_draw"];
const AGE_BANDS = ["18–24","25–34","35–44","45–54","55–64","65+"];
const GENDERS = ["Male","Female","Non-binary","Prefer not to say"];
const CITY_TIERS = ["Tier 1","Tier 2","Tier 3","Rural"];
const INDIA_LANGUAGES = ["English","Hindi","Tamil","Telugu","Marathi","Bengali","Gujarati","Kannada","Malayalam","Punjabi","Odia","Other"];
// These values MUST exactly match what participants select in Registration.js
const EDUCATION = ["High School or below","Diploma / Vocational","Graduate (Bachelor's)",
  "Post Graduate (Master's)","Doctorate / PhD","Other"];
const INCOME_BANDS = ["Prefer not to say","Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA","₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA"];
const PROFESSIONS = ["Student","Salaried — Private Sector","Salaried — Government / PSU",
  "Business Owner / Self Employed","Freelancer / Consultant",
  "Homemaker","Retired","Healthcare Professional",
  "IT / Tech Professional","Finance / Banking","Other"];
const HOUSEHOLD_ROLES = ["Primary decision maker","Joint decision maker","Influencer (input but not final call)","Not involved in household decisions"];
const YEARS_WORKING = ["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"];
const INDUSTRIES = ["FMCG / Consumer Goods","Automobile","Financial Services","Healthcare / Pharma",
  "Real Estate","EdTech / Education","Retail / E-commerce","Travel & Hospitality",
  "Technology / SaaS","Media & Entertainment","Agriculture","Manufacturing","None in particular"];

const SURVEY_TEMPLATES = [
  { id:"nps", label:"NPS", category:"Customer Experience", surveyType:"nps", estimatedMins:5,
    questions:[
      {questionText:"On a scale of 0–10, how likely are you to recommend us to a friend or colleague?",questionType:"rating_scale",helpText:"Probe for reasons behind the score."},
      {questionText:"What is the primary reason for your score?",questionType:"open",helpText:""},
      {questionText:"What could we do to improve your experience?",questionType:"open",helpText:""},
    ]},
  { id:"brand_health", label:"Brand Health", category:"Brand", surveyType:"brand_tracking", estimatedMins:10,
    questions:[
      {questionText:"Which brands in this category are you aware of?",questionType:"open",helpText:"Explore spontaneous brand recall."},
      {questionText:"Which of these brands have you used in the last 3 months?",questionType:"open",helpText:""},
      {questionText:"How would you describe your overall impression of [Brand]?",questionType:"open",helpText:""},
      {questionText:"What attributes do you associate with [Brand]?",questionType:"open",helpText:""},
    ]},
  { id:"product_feedback", label:"Product Feedback", category:"Product", surveyType:"product_feedback", estimatedMins:10,
    questions:[
      {questionText:"How long have you been using this product?",questionType:"open",helpText:""},
      {questionText:"What do you like most about the product?",questionType:"open",helpText:"Probe for specific features."},
      {questionText:"What are the biggest pain points or frustrations?",questionType:"open",helpText:""},
      {questionText:"What features would you like to see added?",questionType:"open",helpText:""},
    ]},
  { id:"concept_testing", label:"Concept Testing", category:"Innovation", surveyType:"concept_testing", estimatedMins:12,
    questions:[
      {questionText:"What is your first reaction to this concept?",questionType:"open",helpText:"Allow unfiltered response first."},
      {questionText:"What do you like about this concept?",questionType:"open",helpText:""},
      {questionText:"What concerns or questions does this raise for you?",questionType:"open",helpText:""},
      {questionText:"How likely would you be to use or buy this if it were available?",questionType:"rating_scale",helpText:""},
    ]},
  { id:"csat", label:"CSAT", category:"Customer Experience", surveyType:"csat", estimatedMins:7,
    questions:[
      {questionText:"How satisfied are you with your recent experience?",questionType:"rating_scale",helpText:""},
      {questionText:"What went well?",questionType:"open",helpText:""},
      {questionText:"What could have been better?",questionType:"open",helpText:""},
    ]},
  { id:"pricing_vw", label:"Pricing — Van Westendorp", category:"Product", surveyType:"pricing_vw", estimatedMins:8,
    questions:[
      {questionText:"At what price would this be so cheap you'd doubt its quality?",questionType:"open",helpText:"Capture the ₹ amount (too cheap)."},
      {questionText:"At what price would this feel like a bargain — great value for money?",questionType:"open",helpText:"Capture the ₹ amount (cheap / good value)."},
      {questionText:"At what price would this start to feel expensive?",questionType:"open",helpText:"Capture the ₹ amount (expensive)."},
      {questionText:"At what price would this be so expensive you would not buy it?",questionType:"open",helpText:"Capture the ₹ amount (too expensive)."},
      {questionText:"What shapes your sense of a fair price for this?",questionType:"open",helpText:""},
    ]},
  { id:"pricing_gg", label:"Pricing — Gabor-Granger", category:"Product", surveyType:"pricing_gg", estimatedMins:6,
    questions:[
      {questionText:"What is the most you would be willing to pay for this?",questionType:"open",helpText:"Capture the maximum ₹ amount (willingness to pay)."},
      {questionText:"Why does that price feel right to you?",questionType:"open",helpText:""},
    ]},
];

// ── API helper ────────────────────────────────────────────────────────────────
async function api(method, path, body) {
  const res = await fetch(`/api/admin${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function SWBtn({children,onClick,variant="primary",disabled,style,type="button"}) {
  const v = {
    primary:{background:C.accent,color:"#000"},
    outline:{background:"transparent",color:C.accent,border:`1.5px solid ${C.accent}`},
    ghost:{background:"transparent",color:C.muted,border:`1.5px solid ${C.border}`},
    danger:{background:C.danger,color:"#fff"},
    sm:{background:C.accent,color:"#000",padding:"5px 12px",fontSize:12},
    smGhost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`,padding:"5px 12px",fontSize:12},
    smDanger:{background:C.danger,color:"#fff",padding:"5px 12px",fontSize:12},
  };
  return <button type={type} disabled={disabled} onClick={onClick} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",lineHeight:1,gap:6,padding:"9px 18px",borderRadius:8,fontSize:13,fontWeight:600,cursor:disabled?"not-allowed":"pointer",border:"none",fontFamily:"inherit",opacity:disabled?0.5:1,...(v[variant]||v.primary),...style}}>{children}</button>;
}

function SWField({label,required,children}) {
  return <div style={{marginBottom:14}}>
    <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.8,marginBottom:5,textTransform:"uppercase"}}>
      {label}{required&&<span style={{color:C.danger,marginLeft:3}}>*</span>}
    </label>
    {children}
  </div>;
}

function SWChips({options,value=[],onChange}) {
  const toggle = o => onChange(value.includes(o)?value.filter(v=>v!==o):[...value,o]);
  return <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
    {options.map(o=>{
      const sel = value.includes(o);
      return <button key={o} type="button" onClick={()=>toggle(o)} style={{padding:"4px 10px",borderRadius:99,fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"inherit",border:`1px solid ${sel?C.accent:C.border}`,background:sel?C.accentDim:"transparent",color:sel?C.accent:C.muted}}>{o}</button>;
    })}
  </div>;
}

const INP = {width:"100%",padding:"9px 11px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none"};
const SEL = {...INP};
const TA  = {...INP,resize:"vertical"};

// ── Step 1: Basic Info ────────────────────────────────────────────────────────
function Step1({data,onChange,projects=[],selectedProjectId,onProjectChange,onCloneApply,multilingualSupport=false,onGenerate,generating,genMsg}) {
  const [showTemplates, setShowTemplates] = useState(false);
  const [showClone, setShowClone]         = useState(false);
  const [cloneSearch, setCloneSearch]     = useState("");
  const [cloneCat, setCloneCat]           = useState("");
  const [cloneType, setCloneType]         = useState("");
  const [allSurveys, setAllSurveys]       = useState([]);
  const [cloneFetching, setCloneFetching] = useState(false);
  const [cloneLoading, setCloneLoading]   = useState(null);

  const openClone = async () => {
    setShowClone(s => {
      if (!s) { fetchAllSurveys(); }
      return !s;
    });
    setShowTemplates(false);
  };

  const fetchAllSurveys = async () => {
    setCloneFetching(true);
    try {
      const res = await api("GET", "/surveys?limit=200");
      setAllSurveys((res.surveys||[]).map(s=>({
        ...s,
        _projectName: s.project?.name || "",
        _customerName: s.customer?.companyName || "",
      })));
    } catch(e) { console.error("Clone fetch error", e); }
    finally { setCloneFetching(false); }
  };

  // Derive distinct category + surveyType options from fetched list
  const catOptions   = [...new Set(allSurveys.map(s=>s.category).filter(Boolean))].sort();
  const typeOptions  = [...new Set(allSurveys.map(s=>s.surveyType).filter(Boolean))].sort();

  const filteredSurveys = allSurveys.filter(s => {
    if (cloneCat  && s.category   !== cloneCat)  return false;
    if (cloneType && s.surveyType !== cloneType)  return false;
    if (cloneSearch) {
      const q = cloneSearch.toLowerCase();
      if (![s.title, s._projectName, s._customerName, s.description, s.purpose]
          .some(f => f && f.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const handleClone = async (s) => {
    setCloneLoading(s.id);
    try {
      const res = await api("GET", `/surveys/${s.id}`);
      const sv = res.survey;
      onCloneApply(sv);
      setShowClone(false);
      setCloneSearch(""); setCloneCat(""); setCloneType("");
    } catch(e) { alert(`Failed to load survey: ${e.message}`); }
    finally { setCloneLoading(null); }
  };

  const applyTemplate = t => {
    onChange({
      ...data,
      category: t.category,
      surveyType: t.surveyType,
      estimatedMins: t.estimatedMins,
      _templateQuestions: t.questions,
    });
    setShowTemplates(false);
  };

  return <div>
    {/* Accelerators row */}
    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
      <SWBtn variant="outline" onClick={()=>{setShowTemplates(s=>!s);setShowClone(false);}}>
        📋 {showTemplates?"Hide Templates":"Use a Template"}
      </SWBtn>
      <SWBtn variant="outline" onClick={openClone}>
        ⧉ {showClone?"Hide":"Clone Existing Survey"}
      </SWBtn>
    </div>

    {/* Template picker */}
    {showTemplates && <div style={{marginBottom:20}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
        {SURVEY_TEMPLATES.map(t=>(
          <div key={t.id} onClick={()=>applyTemplate(t)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:12,cursor:"pointer"}}>
            <div style={{fontWeight:600,color:C.accent,fontSize:13}}>{t.label}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:3}}>{t.questions.length} questions · {t.estimatedMins} min</div>
          </div>
        ))}
      </div>
    </div>}

    {/* Clone picker */}
    {showClone && <div style={{marginBottom:20,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
      <div style={{fontWeight:600,color:C.text,fontSize:13,marginBottom:10}}>
        Clone any survey — all questions and settings will be copied as a starting point.
      </div>
      {/* Filters row */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
        <select style={SEL} value={cloneCat} onChange={e=>setCloneCat(e.target.value)}>
          <option value="">All Categories</option>
          {catOptions.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select style={SEL} value={cloneType} onChange={e=>setCloneType(e.target.value)}>
          <option value="">All Survey Types</option>
          {typeOptions.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <input
        style={{...INP,marginBottom:12}}
        placeholder="Search by title, project, customer, description or research purpose…"
        value={cloneSearch}
        onChange={e=>setCloneSearch(e.target.value)}
        autoFocus
      />
      {cloneFetching
        ? <div style={{fontSize:12,color:C.muted}}>Loading surveys…</div>
        : allSurveys.length===0
          ? <div style={{fontSize:12,color:C.muted}}>No surveys available yet.</div>
          : filteredSurveys.length===0
            ? <div style={{fontSize:12,color:C.muted}}>No surveys match your filters.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:300,overflowY:"auto"}}>
                {filteredSurveys.map(s=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,background:C.surface,borderRadius:8,padding:"10px 12px"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,color:C.text,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.title}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                        {s._customerName&&<span>{s._customerName} · </span>}
                        {s._projectName&&<span>📁 {s._projectName} · </span>}
                        {s.category&&<span>{s.category} · </span>}
                        {s.status}
                      </div>
                    </div>
                    <SWBtn variant="sm" disabled={cloneLoading===s.id} onClick={()=>handleClone(s)}>
                      {cloneLoading===s.id?"Loading…":"⧉ Clone"}
                    </SWBtn>
                  </div>
                ))}
              </div>
      }
    </div>}


    <SWField label="Survey Title" required>
      <input style={INP} value={data.title} onChange={e=>onChange({...data,title:e.target.value})} placeholder="e.g. Brand Awareness Study Q1 2026"/>
    </SWField>

    <SWField label="Project" required>
      {projects.length===0
        ? <div style={{fontSize:12,color:C.muted,padding:"9px 11px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6}}>No projects yet — create a project first from the Projects tab.</div>
        : <select style={SEL} value={selectedProjectId||""} onChange={e=>onProjectChange(e.target.value||null)}>
            <option value="">— Select a project —</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
      }
    </SWField>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <SWField label="Category">
        <select style={SEL} value={data.category} onChange={e=>onChange({...data,category:e.target.value})}>
          {!CATEGORIES.includes(data.category) && data.category && <option>{data.category}</option>}
          {CATEGORIES.map(c=><option key={c}>{c}</option>)}
        </select>
      </SWField>
      <SWField label="Survey Type">
        <select style={SEL} value={data.surveyType} onChange={e=>onChange({...data,surveyType:e.target.value})}>
          {!SURVEY_TYPES.some(t=>t.value===data.surveyType) && data.surveyType && <option value={data.surveyType}>{data.surveyType.replace(/_/g," ")}</option>}
          {SURVEY_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </SWField>
    </div>

    {/* Agent card picker */}
    <SWField label="AI Interviewer Agent" required>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginTop:4}}>
        {AGENTS.map(key=>{
          const m = AGENT_META[key];
          const selected = (data.agent||"jal") === key;
          const recommended = (data.surveyType) && (m.recommendedFor||[]).includes(data.surveyType);
          return (
            <div key={key} onClick={()=>onChange({...data,agent:key})} style={{
              background: selected ? `${m.color}18` : C.surface,
              border:`2px solid ${selected?m.color:C.border}`,
              borderRadius:10, padding:"12px 10px", cursor:"pointer",
              transition:"all 0.15s", position:"relative",
              boxShadow: selected ? `0 0 12px ${m.color}44` : "none",
            }}>
              {recommended&&!selected&&<div style={{position:"absolute",top:6,right:6,fontSize:9,fontWeight:700,color:m.color,background:`${m.color}22`,borderRadius:99,padding:"1px 6px",letterSpacing:0.5}}>★ Recommended</div>}
              {selected&&<div style={{position:"absolute",top:6,right:6,fontSize:9,fontWeight:700,color:m.color,background:`${m.color}22`,borderRadius:99,padding:"1px 6px"}}>✓ Selected</div>}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <img src={m.avatar} alt={m.name} style={{width:36,height:36,borderRadius:"50%",border:`2px solid ${selected?m.color:C.border}`,objectFit:"cover"}}/>
                <div style={{fontWeight:700,fontSize:13,color:m.color}}>{m.name}</div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:C.text,marginBottom:6}}>{m.mode}</div>
              <div style={{fontSize:9,color:C.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Best suited for</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                {m.bestFor.map(tag=>(
                  <span key={tag} style={{fontSize:9,padding:"2px 5px",borderRadius:99,background:selected?`${m.color}22`:C.card,color:selected?m.color:C.muted,fontWeight:600}}>{tag}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </SWField>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <SWField label="Modality">
        <select style={SEL} value={data.modality} onChange={e=>onChange({...data,modality:e.target.value})}>
          {MODALITIES.map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
        </select>
      </SWField>
      <SWField label="Multilingual">
        <div style={{display:"flex",alignItems:"center",gap:10,height:38}}>
          <button
            type="button"
            onClick={()=>onChange({...data,multilingualEnabled:!data.multilingualEnabled})}
            disabled={!multilingualSupport}
            style={{
              width:44,height:24,borderRadius:12,border:"none",cursor:multilingualSupport?"pointer":"not-allowed",
              background:data.multilingualEnabled&&multilingualSupport?C.success:"#374151",
              position:"relative",transition:"background 0.2s",flexShrink:0,
            }}
          >
            <span style={{
              position:"absolute",top:3,left:data.multilingualEnabled&&multilingualSupport?22:3,
              width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",
            }}/>
          </button>
          <span style={{fontSize:12,color:!multilingualSupport?C.muted:data.multilingualEnabled?C.success:C.muted}}>
            {!multilingualSupport?"Platform multilingual is off":data.multilingualEnabled?"Enabled — interview in participant's preferred language":"Disabled — English only"}
          </span>
        </div>
      </SWField>
    </div>

    <SWField label="Estimated Duration (minutes)">
      <input style={INP} type="number" min="1" max="60" value={data.estimatedMins} onChange={e=>onChange({...data,estimatedMins:e.target.value})}/>
    </SWField>

    <SWField label="Description (shown to participant)">
      <textarea style={TA} rows={3} value={data.description} onChange={e=>onChange({...data,description:e.target.value})} placeholder="Brief overview of what the survey is about…"/>
    </SWField>

    <SWField label="Research Purpose (internal only)">
      <textarea style={TA} rows={2} value={data.purpose} onChange={e=>onChange({...data,purpose:e.target.value})} placeholder="Internal objective — not shown to participant…"/>
    </SWField>

    <SWField label="Pre-interview Instructions (shown on setup screen)">
      <textarea style={TA} rows={2} value={data.instructions} onChange={e=>onChange({...data,instructions:e.target.value})} placeholder="Instructions shown to participant before the interview begins…"/>
    </SWField>
    {onGenerate && <div style={{marginTop:16,paddingTop:16,borderTop:"1px solid #273449"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <SWBtn onClick={onGenerate} disabled={generating||!data.surveyType}>{generating?"Generating…":"✦ Generate questionnaire with AI"}</SWBtn>
        <span style={{fontSize:12,color:"#8894A8",flex:1,minWidth:200}}>Builds a methodology-valid {(data.surveyType||"").replace(/_/g," ")} questionnaire from your brief — required questions added automatically, everything editable.</span>
      </div>
      {genMsg && <div style={{marginTop:10,fontSize:13,color:genMsg.startsWith("Generation failed")?"#EF4444":"#22D3EE"}}>{genMsg}</div>}
    </div>}
  </div>;
}

export { Step1 };

// ── Step 2: Questions ─────────────────────────────────────────────────────────
function Step2({questions,setQuestions}) {
  const add = () => setQuestions(qs=>[...qs,{id:Date.now(),questionText:"",questionType:"open",helpText:"",isScreener:false,screenerCondition:"",skipLogic:[],stimulusUrl:null,stimulusType:null,stimulusAlt:""}]);
  const remove = id => setQuestions(qs=>qs.length>1?qs.filter(q=>q.id!==id):qs);
  const update = (id,field,val) => setQuestions(qs=>qs.map(q=>q.id===id?{...q,[field]:val}:q));
  const move = (idx,dir) => setQuestions(qs=>{ const a=[...qs]; const b=a[idx+dir]; a[idx+dir]=a[idx]; a[idx]=b; return a; });
  const addSkip = id => setQuestions(qs=>qs.map(q=>q.id===id&&(q.skipLogic||[]).length<3?{...q,skipLogic:[...q.skipLogic,{condition:"",skipToIndex:-1}]}:q));
  const updateSkip = (id,ri,field,val) => setQuestions(qs=>qs.map(q=>q.id===id?{...q,skipLogic:q.skipLogic.map((r,i)=>i===ri?{...r,[field]:val}:r)}:q));
  const removeSkip = (id,ri) => setQuestions(qs=>qs.map(q=>q.id===id?{...q,skipLogic:q.skipLogic.filter((_,i)=>i!==ri)}:q));

  const uploadStimulus = async (id,file) => {
    update(id,"stimulusUploading",true);
    try {
      const fd = new FormData(); fd.append("file",file);
      const res = await fetch("/api/admin/upload/stimulus",{method:"POST",credentials:"include",body:fd});
      const d = await res.json();
      if(!res.ok) throw new Error(d.error||"Upload failed");
      setQuestions(qs=>qs.map(q=>q.id===id?{...q,stimulusUrl:d.url,stimulusType:d.type,stimulusUploading:false}:q));
    } catch(e){ update(id,"stimulusUploading",false); alert(`Upload failed: ${e.message}`); }
  };

  return <div>
    {questions.map((q,idx)=>(
      <div key={q.id} style={{background:C.card,border:`1px solid ${q.isRequiredRole?C.accent:q.isScreener?"#F59E0B":C.border}`,borderRadius:10,padding:16,marginBottom:12,position:"relative"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <span style={{background:C.surface,color:C.accent,fontWeight:700,fontSize:12,padding:"2px 8px",borderRadius:99,flexShrink:0}}>Q{idx+1}</span>
          {q.isRequiredRole&&<span title="Required for your study type's metric" style={{background:"rgba(79,110,247,0.18)",color:C.accent,fontWeight:700,fontSize:10,padding:"2px 8px",borderRadius:99,textTransform:"uppercase",letterSpacing:0.3,flexShrink:0}}>🔒 Required · {(q.measureKey||q.measurementRole||"").replace(/_/g," ")}</span>}
          {q.stimulusNeed&&<span title="This question benefits from showing a stimulus — attach an image or video below" style={{background:q.stimulusNeed==="required"?"rgba(245,158,11,0.18)":"rgba(136,148,168,0.15)",color:q.stimulusNeed==="required"?"#F59E0B":C.muted,fontWeight:700,fontSize:10,padding:"2px 8px",borderRadius:99,textTransform:"uppercase",letterSpacing:0.3,flexShrink:0}}>📎 Stimulus {q.stimulusNeed}</span>}
          <select style={{...SEL,flex:1,maxWidth:160}} value={q.questionType} onChange={e=>update(q.id,"questionType",e.target.value)}>
            {QUESTION_TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
          </select>
          <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12,color:q.isScreener?"#F59E0B":C.muted,flexShrink:0}}>
            <input type="checkbox" checked={q.isScreener} onChange={e=>update(q.id,"isScreener",e.target.checked)}/> Screener
          </label>
          <div style={{marginLeft:"auto",display:"flex",gap:4}}>
            {idx>0&&<SWBtn variant="smGhost" onClick={()=>move(idx,-1)}>↑</SWBtn>}
            {idx<questions.length-1&&<SWBtn variant="smGhost" onClick={()=>move(idx,1)}>↓</SWBtn>}
            <SWBtn variant="smDanger" onClick={()=>{ if(q.isRequiredRole&&!window.confirm("This question is required for your study type's metric — removing it may block publishing. Remove anyway?"))return; remove(q.id); }}>×</SWBtn>
          </div>
        </div>

        <SWField label="Question Text" required>
          <textarea style={TA} rows={2} value={q.questionText} onChange={e=>update(q.id,"questionText",e.target.value)} placeholder="Enter your question…"/>
        </SWField>

        <SWField label="AI Probing Hint (internal — not shown to participant)">
          <input style={INP} value={q.helpText} onChange={e=>update(q.id,"helpText",e.target.value)} placeholder="e.g. Probe for specific brands mentioned…"/>
        </SWField>

        {q.isScreener&&<SWField label="Screener Pass Condition">
          <input style={INP} value={q.screenerCondition} onChange={e=>update(q.id,"screenerCondition",e.target.value)} placeholder="e.g. Pass if participant mentions owning a car"/>
        </SWField>}

        {/* Stimulus */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.8,marginBottom:6,textTransform:"uppercase"}}>Stimulus (Image / Video)</div>
          {q.stimulusUrl ? (
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              {q.stimulusType==="image"
                ? <img src={q.stimulusUrl} alt="" style={{width:80,height:60,objectFit:"cover",borderRadius:6,border:`1px solid ${C.border}`}}/>
                : <video src={q.stimulusUrl} style={{width:120,height:70,borderRadius:6}} controls/>}
              <div style={{flex:1}}>
                <input style={INP} value={q.stimulusAlt} onChange={e=>update(q.id,"stimulusAlt",e.target.value)} placeholder="Caption shown to participant…"/>
                <SWBtn variant="smDanger" style={{marginTop:6}} onClick={()=>setQuestions(qs=>qs.map(q2=>q2.id===q.id?{...q2,stimulusUrl:null,stimulusType:null,stimulusAlt:""}:q2))}>Remove</SWBtn>
              </div>
            </div>
          ) : (
            <label style={{display:"inline-flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:C.muted,border:`1px dashed ${C.border}`,borderRadius:6,padding:"6px 12px"}}>
              {q.stimulusUploading?"Uploading…":"📎 Attach Image or Video"}
              {!q.stimulusUploading&&<input type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm" style={{display:"none"}} onChange={e=>{if(e.target.files[0])uploadStimulus(q.id,e.target.files[0]);}}/>}
            </label>
          )}
        </div>

        {/* Skip logic */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.8,marginBottom:6,textTransform:"uppercase"}}>Skip Logic</div>
          {(q.skipLogic||[]).map((rule,ri)=>(
            <div key={ri} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
              <span style={{fontSize:12,color:C.muted,flexShrink:0}}>If answer is</span>
              <input style={{...INP,flex:1}} placeholder='e.g. "No" or "Disagree"' value={rule.condition} onChange={e=>updateSkip(q.id,ri,"condition",e.target.value)}/>
              <span style={{fontSize:12,color:C.muted,flexShrink:0}}>→ skip to</span>
              <select style={{...SEL,width:130}} value={rule.skipToIndex} onChange={e=>updateSkip(q.id,ri,"skipToIndex",Number(e.target.value))}>
                <option value={-1}>End survey</option>
                {questions.map((tq,ti)=>ti>idx&&<option key={ti} value={ti}>Q{ti+1}</option>)}
              </select>
              <SWBtn variant="smDanger" onClick={()=>removeSkip(q.id,ri)}>×</SWBtn>
            </div>
          ))}
          {(q.skipLogic||[]).length<3&&<SWBtn variant="smGhost" onClick={()=>addSkip(q.id)}>+ Add Skip Rule</SWBtn>}
        </div>
      </div>
    ))}
    <SWBtn variant="outline" onClick={add}>+ Add Question</SWBtn>
  </div>;
}

// ── Step 3: Targeting ─────────────────────────────────────────────────────────
function Step3({data,onChange}) {
  const set = (field,val) => onChange({...data,[field]:val});
  const [newQuota,setNewQuota] = useState({dimension:"Gender",value:"",quota:""});

  const addQuota = () => {
    if(!newQuota.value||!newQuota.quota) return;
    onChange({...data,segmentQuotas:[...(data.segmentQuotas||[]),{...newQuota,quota:Number(newQuota.quota)}]});
    setNewQuota({dimension:"Gender",value:"",quota:""});
  };
  const removeQuota = i => onChange({...data,segmentQuotas:(data.segmentQuotas||[]).filter((_,j)=>j!==i)});

  return <div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:4}}>
      <SWField label="Target Sample Size">
        <input style={INP} type="number" min="1" value={data.targetSampleSize} onChange={e=>set("targetSampleSize",e.target.value)} placeholder="e.g. 100"/>
      </SWField>
      <SWField label="Close Date">
        <input style={{...INP,colorScheme:"dark"}} type="date" value={data.closeDate} onChange={e=>set("closeDate",e.target.value)}/>
      </SWField>
      <SWField label="Incentive Type">
        <select style={SEL} value={data.incentiveType} onChange={e=>set("incentiveType",e.target.value)}>
          {INCENTIVE_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
        </select>
      </SWField>
    </div>
    {data.incentiveType!=="none"&&<SWField label="Incentive Value">
      <input style={INP} value={data.incentiveValue} onChange={e=>set("incentiveValue",e.target.value)} placeholder="e.g. ₹200 Amazon voucher"/>
    </SWField>}

    <div style={{fontSize:13,fontWeight:700,color:C.text,margin:"16px 0 10px"}}>Demographics</div>
    <SWField label="Age Bands"><SWChips options={AGE_BANDS} value={data.targetAgeBands} onChange={v=>set("targetAgeBands",v)}/></SWField>
    <SWField label="Gender"><SWChips options={GENDERS} value={data.targetGenders} onChange={v=>set("targetGenders",v)}/></SWField>
    <SWField label="City Tier"><SWChips options={CITY_TIERS} value={data.targetCityTiers} onChange={v=>set("targetCityTiers",v)}/></SWField>
    <div style={{fontSize:13,fontWeight:700,color:C.text,margin:"16px 0 10px"}}>Profile Filters</div>
    <SWField label="Education"><SWChips options={EDUCATION} value={data.targetEducation} onChange={v=>set("targetEducation",v)}/></SWField>
    <SWField label="Income Band"><SWChips options={INCOME_BANDS} value={data.targetIncomeBands} onChange={v=>set("targetIncomeBands",v)}/></SWField>
    <SWField label="Profession"><SWChips options={PROFESSIONS} value={data.targetProfessions} onChange={v=>set("targetProfessions",v)}/></SWField>
    <SWField label="Household Role"><SWChips options={HOUSEHOLD_ROLES} value={data.targetHouseholdRoles} onChange={v=>set("targetHouseholdRoles",v)}/></SWField>
    <SWField label="Years Working"><SWChips options={YEARS_WORKING} value={data.targetYearsWorking} onChange={v=>set("targetYearsWorking",v)}/></SWField>
    <SWField label="Industries"><SWChips options={INDUSTRIES} value={data.targetIndustries} onChange={v=>set("targetIndustries",v)}/></SWField>

    <div style={{fontSize:13,fontWeight:700,color:C.text,margin:"16px 0 10px"}}>Segment Quotas</div>
    <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:10}}>
      <SWField label="Dimension" style={{flex:1}}>
        <select style={SEL} value={newQuota.dimension} onChange={e=>setNewQuota(q=>({...q,dimension:e.target.value}))}>
          {["Gender","Age Band","City Tier","Education","Income Band","Profession","Household Role","Language"].map(d=><option key={d}>{d}</option>)}
        </select>
      </SWField>
      <SWField label="Value" style={{flex:2}}><input style={INP} value={newQuota.value} onChange={e=>setNewQuota(q=>({...q,value:e.target.value}))} placeholder='e.g. "Male"'/></SWField>
      <SWField label="Cap (completions)" style={{flex:1}}><input style={INP} type="number" min="1" value={newQuota.quota} onChange={e=>setNewQuota(q=>({...q,quota:e.target.value}))}/></SWField>
      <SWBtn variant="sm" onClick={addQuota} style={{marginBottom:14}}>Add</SWBtn>
    </div>
    {(data.segmentQuotas||[]).length>0&&<div>
      {(data.segmentQuotas||[]).map((q,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",marginBottom:6}}>
          <span style={{fontSize:12,color:C.muted}}>{q.dimension}</span>
          <span style={{fontSize:13,color:C.text,fontWeight:600}}>{q.value}</span>
          <span style={{marginLeft:"auto",fontSize:12,color:C.accent}}>{q.quota} completions max</span>
          <SWBtn variant="smDanger" onClick={()=>removeQuota(i)}>×</SWBtn>
        </div>
      ))}
    </div>}
  </div>;
}

export { Step2, Step3 };

// ── Step 4: Review ────────────────────────────────────────────────────────────
function Step4({basic,questions,targeting}) {
  const Section = ({title,children})=><div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:12}}>
    <div style={{fontSize:12,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>{title}</div>
    {children}
  </div>;
  const Row = ({label,val})=>val?<div style={{display:"flex",gap:12,marginBottom:4,fontSize:13}}><span style={{color:C.muted,minWidth:120}}>{label}</span><span style={{color:C.text}}>{val}</span></div>:null;
  const Tags = ({label,arr})=>arr?.length?<div style={{marginBottom:6}}><span style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.8}}>{label}: </span>{arr.map(v=><span key={v} style={{fontSize:11,background:C.accentDim,color:C.accent,padding:"2px 8px",borderRadius:99,marginLeft:4}}>{v}</span>)}</div>:null;

  return <div>
    <Section title="Basic Info">
      <Row label="Title" val={basic.title}/>
      <Row label="Category" val={basic.category}/>
      <Row label="Type" val={basic.surveyType?.replace(/_/g," ")}/>
      <Row label="Agent" val={basic.agent}/>
      <Row label="Modality" val={basic.modality}/>
      <Row label="Multilingual" val={basic.multilingualEnabled?"Enabled":"Disabled"}/>
      <Row label="Duration" val={`${basic.estimatedMins} min`}/>
      {basic.description&&<div style={{marginTop:8,fontSize:12,color:C.muted}}>{basic.description}</div>}
    </Section>

    <Section title={`Questions (${questions.length})`}>
      {questions.map((q,i)=>(
        <div key={q.id} style={{borderBottom:`1px solid ${C.border}`,paddingBottom:10,marginBottom:10}}>
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
            <span style={{color:C.accent,fontWeight:700,fontSize:12}}>Q{i+1}</span>
            <span style={{fontSize:11,color:C.muted}}>{q.questionType.replace(/_/g," ")}</span>
            {q.isScreener&&<span style={{fontSize:10,background:"#F59E0B22",color:"#F59E0B",padding:"1px 6px",borderRadius:99}}>Screener</span>}
          </div>
          <div style={{fontSize:13,color:C.text}}>{q.questionText}</div>
          {q.stimulusUrl&&<div style={{fontSize:11,color:C.info,marginTop:4}}>📎 {q.stimulusType} attached{q.stimulusAlt?` — "${q.stimulusAlt}"`:"" }</div>}
          {(q.skipLogic||[]).filter(r=>r.condition).map((r,ri)=>(
            <div key={ri} style={{fontSize:11,color:C.info,fontStyle:"italic",marginTop:3}}>
              ↳ If "{r.condition}" → {r.skipToIndex===-1?"End survey":`skip to Q${r.skipToIndex+1}`}
            </div>
          ))}
        </div>
      ))}
    </Section>

    <Section title="Targeting">
      <Row label="Sample Size" val={targeting.targetSampleSize}/>
      <Row label="Close Date" val={targeting.closeDate}/>
      <Row label="Incentive" val={targeting.incentiveType!=="none"?`${targeting.incentiveType}${targeting.incentiveValue?` — ${targeting.incentiveValue}`:""}`:null}/>
      <Tags label="Age Bands" arr={targeting.targetAgeBands}/>
      <Tags label="Gender" arr={targeting.targetGenders}/>
      <Tags label="City Tier" arr={targeting.targetCityTiers}/>
      <Tags label="Education" arr={targeting.targetEducation}/>
      <Tags label="Income" arr={targeting.targetIncomeBands}/>
      <Tags label="Profession" arr={targeting.targetProfessions}/>
      <Tags label="Industries" arr={targeting.targetIndustries}/>
      {!targeting.targetAgeBands?.length&&!targeting.targetGenders?.length&&!targeting.targetCityTiers?.length&&
        <div style={{fontSize:12,color:C.muted}}>No demographic filters — open to all participants</div>}
      {(targeting.segmentQuotas||[]).length>0&&<div style={{marginTop:8}}>
        <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Segment Quotas:</div>
        {(targeting.segmentQuotas||[]).map((q,i)=><div key={i} style={{fontSize:12,color:C.text}}>{q.dimension}: {q.value} — max {q.quota}</div>)}
      </div>}
    </Section>
  </div>;
}

// ── Main SurveyWizard Component ───────────────────────────────────────────────
const STEPS = ["Basic Info","Questions","Targeting","Review"];

const defaultBasic = () => ({
  title:"", category:"Brand", surveyType:"nps",
  agent:"jal", modality:"voice", multilingualEnabled:false, estimatedMins:10,
  description:"", purpose:"", instructions:"",
});
const defaultTargeting = () => ({
  targetSampleSize:"", closeDate:"", incentiveType:"none", incentiveValue:"",
  segmentQuotas:[], targetAgeBands:[], targetGenders:[], targetCityTiers:[],
  targetEducation:[], targetIncomeBands:[], targetProfessions:[],
  targetHouseholdRoles:[], targetYearsWorking:[], targetIndustries:[],
});
const defaultQuestion = () => ({
  id:Date.now(), questionText:"", questionType:"open", helpText:"",
  isScreener:false, screenerCondition:"", skipLogic:[],
  stimulusUrl:null, stimulusType:null, stimulusAlt:"",
  measurementRole:null, measureKey:null, scaleMin:null, scaleMax:null, choices:null, isRequiredRole:false, linkedToOrderIndex:null,
});

export default function SurveyWizard({customerId, projectId: initialProjectId, projects=[], prefill, onDone, onCancel}) {
  const isEdit = !!prefill?.editId;
  // Lifecycle gating: new (unsaved) → draft → pending_review → approved → active.
  // Each footer action unlocks only once the prior state is reached.
  const curStatus = isEdit ? (prefill?.status || "draft") : "new";

  const [step, setStep] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId||prefill?.projectId||null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // synchronous guard — blocks double-submit before React re-renders the disabled button
  const [err, setErr]  = useState("");
  // After publish, show invite modal before sending invitations
  const [inviteModal, setInviteModal] = useState(null); // null | { survey, preview, inviting, result }

  const [multilingualSupport, setMultilingualSupport] = useState(false);

  useEffect(() => {
    api("GET", "/config").then(d => {
      if (d?.multilingualSupport !== undefined) setMultilingualSupport(d.multilingualSupport);
    }).catch(()=>{});
  }, []); // eslint-disable-line

  const [basic, setBasic] = useState(() => prefill ? {
    title: prefill.title||"", category: prefill.category||"Brand Research",
    surveyType: prefill.surveyType||"brand_tracking", agent: prefill.agent||"aria",
    modality: prefill.modality||"voice", multilingualEnabled: prefill.multilingualEnabled||false,
    estimatedMins: prefill.estimatedMins||10, description: prefill.description||"",
    purpose: prefill.purpose||"", instructions: prefill.instructions||"",
  } : defaultBasic());

  const [questions, setQuestions] = useState(() => prefill?.questions?.length
    ? prefill.questions.map((q,i)=>({...defaultQuestion(),id:Date.now()+i,...q,skipLogic:q.skipLogic||[]}))
    : [defaultQuestion()]);

  const [targeting, setTargeting] = useState(() => prefill ? {
    targetSampleSize: prefill.targetSampleSize!=null?String(prefill.targetSampleSize):"",
    closeDate: prefill.closeDate?prefill.closeDate.split("T")[0]:"",
    incentiveType: prefill.incentiveType||"none",
    incentiveValue: prefill.incentiveValue||"",
    segmentQuotas: prefill.segmentQuotas||[],
    targetAgeBands: prefill.targetAgeBands||[], targetGenders: prefill.targetGenders||[],
    targetCityTiers: prefill.targetCityTiers||[],
    targetEducation: prefill.targetEducation||[], targetIncomeBands: prefill.targetIncomeBands||[],
    targetProfessions: prefill.targetProfessions||[], targetHouseholdRoles: prefill.targetHouseholdRoles||[],
    targetYearsWorking: prefill.targetYearsWorking||[], targetIndustries: prefill.targetIndustries||[],
  } : defaultTargeting());

  // Apply template questions from Step1 template picker
  useEffect(() => {
    if (basic._templateQuestions) {
      setQuestions(basic._templateQuestions.map((q,i)=>({...defaultQuestion(),id:Date.now()+i,...q})));
      setBasic(b=>{ const nb={...b}; delete nb._templateQuestions; return nb; });
    }
  }, [basic._templateQuestions]);

  const validate = () => {
    if (!basic.title.trim()) { setErr("Survey title is required."); setStep(0); return false; }
    if (questions.some(q=>!q.questionText.trim())) { setErr("All questions must have text."); setStep(1); return false; }
    return true;
  };

  const buildPayload = () => ({
    customerId, projectId: selectedProjectId||null,
    ...basic,
    estimatedMins: Number(basic.estimatedMins)||10,
    ...targeting,
    targetSampleSize: targeting.targetSampleSize ? Number(targeting.targetSampleSize) : null,
    incentiveType: targeting.incentiveType!=="none" ? targeting.incentiveType : null,
    incentiveValue: targeting.incentiveValue||null,
    closeDate: targeting.closeDate||null,
    questions: questions.map(q=>({
      questionText: q.questionText,
      questionType: q.questionType,
      helpText: q.helpText||null,
      isScreener: q.isScreener||false,
      screenerCondition: q.screenerCondition||null,
      skipLogic: (q.skipLogic||[]).filter(r=>r.condition?.trim()),
      stimulusUrl: q.stimulusUrl||null,
      stimulusType: q.stimulusType||null,
      stimulusAlt: q.stimulusAlt||null,
      measurementRole: q.measurementRole||null, measureKey: q.measureKey||null,
      scaleMin: q.scaleMin??null, scaleMax: q.scaleMax??null, choices: q.choices||null,
      isRequiredRole: !!q.isRequiredRole, linkedToOrderIndex: q.linkedToOrderIndex??null,
    })),
  });

  // AI Study Compiler — brief → methodology-valid editable questionnaire.
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg]         = useState("");
  const handleGenerate = async () => {
    setGenerating(true); setGenMsg("");
    try {
      const out = await api("POST", "/surveys/compile", {
        studyType: basic.surveyType, category: basic.category,
        researchPurpose: basic.purpose, description: basic.description,
        estimatedMins: Number(basic.estimatedMins) || undefined,
        language: basic.multilingualEnabled ? "multilingual" : "English",
      });
      setQuestions((out.questions || []).map((q, i) => ({
        ...defaultQuestion(), id: Date.now() + i,
        questionText: q.questionText, questionType: q.questionType, helpText: q.helpText || "",
        measurementRole: q.measurementRole || null, measureKey: q.measureKey || null,
        scaleMin: q.scaleMin ?? null, scaleMax: q.scaleMax ?? null, choices: q.choices || null,
        isRequiredRole: !!q.isRequiredRole, linkedToOrderIndex: q.linkedToOrderIndex ?? null,
        isScreener: !!q.isScreener, screenerCondition: q.screenerCondition || "",
        skipLogic: Array.isArray(q.skipLogic) ? q.skipLogic : [],
        stimulusAlt: q.stimulusHint || "", stimulusNeed: q.stimulusNeed || null,
      })));
      if (out.recommendedPersona) setBasic((b) => ({ ...b, agent: out.recommendedPersona }));
      setGenMsg(`Generated ${out.questions?.length || 0} questions in the Questions step — review & edit.`);
    } catch (e) { setGenMsg("Generation failed: " + e.message); }
    finally { setGenerating(false); }
  };

  // mode: "draft" | "publish" | "publishAndInvite"
  const save = async (mode="draft") => {
    if (!validate()) return;
    // Synchronous re-entrancy guard. The disabled={saving} attribute only takes
    // effect after React re-renders, leaving a window where a rapid second click
    // (common on a slow connection) fires save() again and creates a duplicate
    // survey — each POST mints a fresh SRV-id server-side. The ref blocks it now.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true); setErr("");
    try {
      const payload = buildPayload();
      let survey;
      if (isEdit) {
        const r = await api("PATCH",`/surveys/${prefill.editId}`,payload);
        survey = r.survey;
      } else {
        const r = await api("POST","/surveys",payload);
        survey = r.survey;
      }
      if (mode==="publish" || mode==="publishAndInvite") {
        await api("POST",`/surveys/${survey.id}/publish`,{});
      }
      if (mode==="sendForReview") {
        // Move draft → pending_review so the customer can approve it in the portal.
        await api("POST",`/surveys/${survey.id}/send-for-review`,{});
      }
      if (mode==="publishAndInvite") {
        // Survey is published. Now fetch matching preview — show the invite modal.
        setSaving(false);
        setInviteModal({ survey, preview: "loading", inviting: false, result: null });
        try {
          const preview = await api("GET", `/surveys/${survey.id}/matching-participants`);
          setInviteModal(m => ({ ...m, preview }));
        } catch(e) {
          setInviteModal(m => ({ ...m, preview: null, error: e.message }));
        }
        return;
      }
      onDone(survey, mode!=="draft");
    } catch(e){ setErr(e.message); } finally { setSaving(false); savingRef.current = false; }
  };

  const confirmInvite = async () => {
    if (!inviteModal?.survey) return;
    setInviteModal(m => ({ ...m, inviting: true }));
    try {
      const result = await api("POST", `/surveys/${inviteModal.survey.id}/invite-matching`, {});
      setInviteModal(m => ({ ...m, inviting: false, result }));
    } catch(e) {
      setInviteModal(m => ({ ...m, inviting: false, error: e.message }));
    }
  };

  // ── Layout ──────────────────────────────────────────────────────────────────
  return <div style={{background:C.bg,minHeight:"100dvh",fontFamily:"'Poppins',sans-serif",color:C.text}}>
    <style>{"* { box-sizing: border-box; } input[type='date']::-webkit-calendar-picker-indicator { filter: invert(0.8); cursor: pointer; }"}</style>

    {/* Invite matching modal (shown after publish, before sending invitations) */}
    {inviteModal && (
      <InviteMatchingModal
        preview={inviteModal.preview}
        inviting={inviteModal.inviting}
        result={inviteModal.result}
        error={inviteModal.error}
        subtitle="Survey has been published. Review the matching results before sending invitations."
        cancelLabel={inviteModal.result ? "Done" : "Cancel (survey stays published)"}
        onCancel={()=>{ setInviteModal(null); onDone(inviteModal.survey, true); }}
        onConfirm={confirmInvite}
      />
    )}

    {/* Header */}
    <div style={{padding:"14px 24px",background:C.surface,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:16,position:"sticky",top:0,zIndex:10}}>
      <button onClick={onCancel} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:22,lineHeight:1,minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
      <div>
        <div style={{fontWeight:700,fontSize:15,color:C.text}}>
          {isEdit?"Edit Survey":"New Survey"}
          {selectedProjectId&&<span style={{fontSize:12,color:C.accent,marginLeft:8,fontWeight:400}}>
            📁 {projects.find(p=>p.id===selectedProjectId)?.name||"Project"}
          </span>}
        </div>
        <div style={{fontSize:12,color:C.muted}}>Step {step+1} of {STEPS.length}: {STEPS[step]}</div>
      </div>
    </div>

    {/* Step pills */}
    <div style={{display:"flex",gap:0,padding:"0 24px",background:C.surface,borderBottom:`1px solid ${C.border}`}}>
      {STEPS.map((s,i)=>(
        <button key={s} onClick={()=>i<step&&setStep(i)} style={{background:"none",border:"none",cursor:i<=step?"pointer":"default",padding:"10px 14px",fontSize:12,fontWeight:600,fontFamily:"inherit",color:i===step?C.accent:i<step?C.text:C.muted,borderBottom:`2px solid ${i===step?C.accent:"transparent"}`,whiteSpace:"nowrap"}}>
          {i<step?"✓ ":""}{s}
        </button>
      ))}
    </div>

    {/* Body */}
    <div style={{padding:24,maxWidth:860,margin:"0 auto"}}>
      {err&&<div style={{background:"#EF444422",border:`1px solid ${C.danger}`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.danger}}>{err}</div>}

      {step===0&&<Step1
        data={basic} onChange={setBasic}
        projects={projects} selectedProjectId={selectedProjectId} onProjectChange={setSelectedProjectId}
        multilingualSupport={multilingualSupport}
        onGenerate={handleGenerate} generating={generating} genMsg={genMsg}
        onCloneApply={sv=>{
          // Pre-fill all wizard state from the cloned survey
          setBasic({
            title:`Copy of ${sv.title}`,
            category:sv.category||"Brand Research",
            surveyType:sv.surveyType||"brand_tracking",
            agent:sv.agent||"aria",
            modality:sv.modality||"voice",
            multilingualEnabled:sv.multilingualEnabled||false,
            estimatedMins:sv.estimatedMins||10,
            description:sv.description||"",
            purpose:sv.purpose||"",
            instructions:sv.instructions||"",
          });
          if (sv.questions?.length) {
            setQuestions(sv.questions.map((q,i)=>({
              ...defaultQuestion(),id:Date.now()+i,
              questionText:q.questionText||"",
              questionType:q.questionType||"open",
              helpText:q.helpText||"",
              isScreener:q.isScreener||false,
              screenerCondition:q.screenerCondition||"",
              skipLogic:Array.isArray(q.skipLogic)?q.skipLogic:[],
              stimulusUrl:q.stimulusUrl||null,
              stimulusType:q.stimulusType||null,
              stimulusAlt:q.stimulusAlt||"",
            })));
          }
          setTargeting({
            targetSampleSize:sv.targetSampleSize!=null?String(sv.targetSampleSize):"",
            closeDate:"", // don't copy close date
            incentiveType:sv.incentiveType||"none",
            incentiveValue:sv.incentiveValue||"",
            segmentQuotas:Array.isArray(sv.segmentQuotas)?sv.segmentQuotas:[],
            targetAgeBands:sv.targetAgeBands||[],
            targetGenders:sv.targetGenders||[],
            targetCityTiers:sv.targetCityTiers||[],
            targetEducation:sv.targetEducation||[],
            targetIncomeBands:sv.targetIncomeBands||[],
            targetProfessions:sv.targetProfessions||[],
            targetHouseholdRoles:sv.targetHouseholdRoles||[],
            targetYearsWorking:sv.targetYearsWorking||[],
            targetIndustries:sv.targetIndustries||[],
          });
          setErr("");
        }}
      />}
      {step===1&&<Step2 questions={questions} setQuestions={setQuestions}/>}
      {step===2&&<Step3 data={targeting} onChange={setTargeting}/>}
      {step===3&&<Step4 basic={basic} questions={questions} targeting={targeting}/>}

      {/* Nav buttons */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:24,paddingTop:20,borderTop:`1px solid ${C.border}`}}>
        <SWBtn variant="ghost" onClick={step===0?onCancel:()=>setStep(s=>s-1)}>
          {step===0?"Cancel":"← Back"}
        </SWBtn>
        <div style={{display:"flex",gap:10}}>
          {step===STEPS.length-1 ? <>
            <SWBtn variant="ghost" disabled={saving} onClick={()=>save("draft")}>{saving?"Saving…":"Save as Draft"}</SWBtn>
            <SWBtn variant="outline"
              disabled={saving||!(curStatus==="draft"||curStatus==="approved")}
              title={curStatus==="new"?"Save as draft first":curStatus==="pending_review"?"Already sent — awaiting customer approval":(curStatus==="draft"||curStatus==="approved")?"Send to the customer for approval":"Only a draft or approved survey can be re-sent for review"}
              onClick={()=>save("sendForReview")}
              style={{borderColor:"#8B5CF6",color:(curStatus==="draft"||curStatus==="approved")?"#8B5CF6":C.muted,cursor:(curStatus==="draft"||curStatus==="approved")?"pointer":"not-allowed"}}>
              {saving?"Sending…":"→ Send for Review"}
            </SWBtn>
            <div title={curStatus!=="approved"?"Customer must approve the survey before it can be published":undefined} style={{display:"contents"}}>
              <SWBtn variant="outline" disabled={saving||curStatus!=="approved"} onClick={()=>save("publish")} style={{color:curStatus==="approved"?C.text:C.muted,borderColor:C.border,cursor:curStatus==="approved"?"pointer":"not-allowed"}}>{saving?"Publishing…":"Publish"}</SWBtn>
              <SWBtn disabled={saving||curStatus!=="approved"} title={curStatus!=="approved"?"Publish the survey before sending invitations":undefined} onClick={()=>save("publishAndInvite")} style={{cursor:curStatus==="approved"?"pointer":"not-allowed",opacity:curStatus==="approved"?1:0.5}}>{saving?"Publishing & inviting…":"Publish & Invite"}</SWBtn>
            </div>
          </> : (
            <SWBtn onClick={()=>{ if(step===0&&!basic.title.trim()){setErr("Title is required.");return;} setErr(""); setStep(s=>s+1); }}>Next →</SWBtn>
          )}
        </div>
      </div>
    </div>
  </div>;
}
